import styled from 'styled-components';
import { Text, crTheme } from '@comicrelief/component-library';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { DirectPaymentErrorType } from '@/types/shared-types';

export const ErrorDetailsWrapper = styled.div`
  margin-top: 1rem;
  width: 100%;
  padding: 1rem;
  background-color: ${crTheme.color('white')};
  border-radius: 0.5rem;
  border: 1px solid ${crTheme.color('grey_medium')};
  max-width: 100%;
  overflow-wrap: break-word;
  text-align: left;
`;

const ErrorSourceItem = styled.div`
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px dashed ${crTheme.color('grey_light')};

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

interface ErrorSources {
  reduxError: string | null;
  locationStateError: string | null;
  rawLocationState: string | null;
}

// Define types for location state to avoid 'any' type issues
interface LocationStateWithError {
  error?: {
    message?: string;
  };
  errorMessage?: string;
}

interface ErrorDetailsProps {
  errorType?: DirectPaymentErrorType;
}

export function ErrorDetails({ errorType }: ErrorDetailsProps = {}) {
  const transactionState = useAppSelector(state => state.transaction);
  const location = useLocation();
  const [errorSources, setErrorSources] = useState<ErrorSources>({
    reduxError: null,
    locationStateError: null,
    rawLocationState: null
  });

  useEffect(() => {
    // Collect error details from various sources
    const reduxError = transactionState?.errorMessage ?? null;

    const locationState = location.state as LocationStateWithError | null;
    const locationStateError = locationState?.error?.message ?? locationState?.errorMessage ?? null;

    let rawLocationState = null;
    try {
      if (locationState) {
        rawLocationState = JSON.stringify(locationState, null, 2);
      }
    } catch (e) {
      rawLocationState = 'Error stringifying location state';
    }

    console.error('Error sources:', {
      reduxError,
      locationStateError,
      rawLocationState
    });

    setErrorSources({
      reduxError,
      locationStateError,
      rawLocationState
    });
  }, [transactionState, location]);

  const getAdditionalErrorInfo = (type?: DirectPaymentErrorType): string | null => {
    switch (type) {
      case 'missing-transaction-id':
        return 'A transactionId hasn\'t been provided for this direct payment, you can\'t navigate to the root of /pay/ without one';
      case 'id-not-found':
        return 'This direct payment wasn\'t found in the payment-delta S3 bucket by the given ID';
      case 'root-not-allowed':
        return 'You\'re in direct payment mode, you can\'t navigate to the root of the site. Change your VITE_DIRECT_PAYMENT_MODE env var';
      default:
        return null;
    }
  };

  const additionalErrorInfo = getAdditionalErrorInfo(errorType);

  return (
    <ErrorDetailsWrapper>
      <Text tag="p" family="Montserrat" size="s" color="red" weight="bold">Non-Production environment error details:</Text>

      <ErrorSourceItem>
        <Text tag="p" family="Montserrat" size="xs" color="black" weight="bold">Redux error:</Text>
        <Text tag="p" family="Montserrat" size="xs" color="black">
          {errorSources.reduxError ?? 'none'}
        </Text>
      </ErrorSourceItem>

      <ErrorSourceItem>
        <Text tag="p" family="Montserrat" size="xs" color="black" weight="bold">Location state error:</Text>
        <Text tag="p" family="Montserrat" size="xs" color="black">
          {errorSources.locationStateError ?? 'none'}
        </Text>
      </ErrorSourceItem>

      <ErrorSourceItem>
        <Text tag="p" family="Montserrat" size="xs" color="black" weight="bold">Raw location state:</Text>
        <Text tag="p" family="Montserrat" size="xs" color="black" style={{ whiteSpace: 'pre-wrap' }}>
          {errorSources.rawLocationState ?? 'none'}
        </Text>
      </ErrorSourceItem>

      {additionalErrorInfo && (
        <ErrorSourceItem>
          <Text tag="p" family="Montserrat" size="md" color="black" weight="bold">✨ ✨ ✨  Bonus error info:  ✨ ✨ ✨</Text>
          <Text tag="p" family="Montserrat" size="xs" color="black">
            {additionalErrorInfo}
          </Text>
        </ErrorSourceItem>
      )}
    </ErrorDetailsWrapper>
  );
}
